import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "enrolled", "archived"];

export default function Admin() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const [a, b] = await Promise.all([
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/inquiries/stats`),
      ]);
      setRows(a.data);
      setStats(b.data);
    } catch (e) {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/inquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const download = () => {
    const header = ["name","phone","email","student_class","board","status","created_at","message"];
    const csv = [header.join(",")].concat(
      rows.map((r) => header.map((h) => JSON.stringify(r[h] ?? "")).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "kothari-inquiries.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const badgeFor = (s) => {
    const map = {
      new: "bg-red-50 text-red-700 border-red-200",
      contacted: "bg-amber-50 text-amber-700 border-amber-200",
      enrolled: "bg-green-50 text-green-700 border-green-200",
      archived: "bg-gray-100 text-gray-500 border-gray-200",
    };
    return `text-[10px] font-bold uppercase tracking-widest px-2 py-1 border ${map[s] || map.new}`;
  };

  return (
    <div className="bg-white min-h-screen" data-testid="admin-page">
      <section className="bg-gray-950 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-end justify-between gap-6">
          <div>
            <span className="text-xs font-bold tracking-[0.22em] text-red-500 uppercase">Internal · Admissions</span>
            <h1 className="font-display mt-2 text-3xl lg:text-4xl font-black tracking-tight">Inquiry Dashboard</h1>
          </div>
          <div className="flex gap-6 text-sm">
            {[
              { k: "Total", v: stats.total },
              { k: "New", v: stats.new },
              { k: "Contacted", v: stats.contacted },
            ].map((s) => (
              <div key={s.k} data-testid={`stat-${s.k.toLowerCase()}`}>
                <div className="text-[10px] uppercase tracking-widest text-gray-400">{s.k}</div>
                <div className="font-display text-2xl font-black text-white">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex justify-between items-center mb-5">
          <h2 className="font-display text-xl font-bold text-gray-950">All inquiries ({rows.length})</h2>
          <div className="flex gap-2">
            <button onClick={load} className="btn-secondary rounded-none text-xs" data-testid="admin-refresh"><RefreshCw className="w-3.5 h-3.5"/>Refresh</button>
            <button onClick={download} className="btn-primary rounded-none text-xs" data-testid="admin-export"><Download className="w-3.5 h-3.5"/>Export CSV</button>
          </div>
        </div>

        <div className="border border-gray-200 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-500 gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="admin-empty">No inquiries yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Board</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`inquiry-row-${r.id}`}>
                    <TableCell className="font-medium">
                      <div>{r.name}</div>
                      {r.email && <div className="text-xs text-gray-500">{r.email}</div>}
                    </TableCell>
                    <TableCell><a className="hover:text-red-600" href={`tel:${r.phone}`}>{r.phone}</a></TableCell>
                    <TableCell>{r.student_class}</TableCell>
                    <TableCell>{r.board}</TableCell>
                    <TableCell><span className={badgeFor(r.status)}>{r.status}</span></TableCell>
                    <TableCell className="text-xs text-gray-500">{new Date(r.created_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                        <SelectTrigger className="h-8 w-36 text-xs rounded-none" data-testid={`status-select-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </section>
    </div>
  );
}
